import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Layout } from '@/components/Layout';

// Lazy load pages for better performance
import { lazy, Suspense } from 'react';

const Home = lazy(() => import('@/pages/Home'));
const Upload = lazy(() => import('@/pages/Upload'));
const RankLab = lazy(() => import('@/pages/RankLab'));
const Replays = lazy(() => import('@/pages/Replays'));
const ReplayDetail = lazy(() => import('@/pages/ReplayDetail'));
const Viewer = lazy(() => import('@/pages/Viewer'));
const JoinParty = lazy(() => import('@/pages/JoinParty'));
const PlayerProfile = lazy(() => import('@/pages/PlayerProfile'));
const ReplayQualityFaq = lazy(() => import('@/pages/ReplayQualityFaq'));
const Cheaters = lazy(() => import('@/pages/Cheaters'));
const News = lazy(() => import('@/pages/News'));
const NewsDetail = lazy(() => import('@/pages/NewsDetail'));

// Auth pages
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const DeviceAuth = lazy(() => import('@/pages/DeviceAuth'));

// User pages
const MyReplays = lazy(() => import('@/pages/MyReplays'));
const Profile = lazy(() => import('@/pages/Profile'));
const ApiTokens = lazy(() => import('@/pages/ApiTokens'));
const ApiDocs = lazy(() => import('@/pages/ApiDocs'));

// Clips pages (024-clip-system)
const BrowseClips = lazy(() => import('@/pages/BrowseClips'));
const MyClips = lazy(() => import('@/pages/Clips'));
const ClipViewer = lazy(() => import('@/pages/ClipViewer'));
const ClipEdit = lazy(() => import('@/pages/ClipEdit'));

// Feedback pages
const FeedbackHub = lazy(() => import('@/pages/FeedbackHub'));
const FeedbackPost = lazy(() => import('@/pages/FeedbackPost'));
const NewFeedback = lazy(() => import('@/pages/NewFeedback'));
const EditFeedback = lazy(() => import('@/pages/EditFeedback'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Contact = lazy(() => import('@/pages/Contact'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const LegalNotice = lazy(() => import('@/pages/LegalNotice'));

// Hidden tools (031-mesh-preview-tool)
const MeshPreview = lazy(() => import('@/pages/tools/MeshPreview'));
const StadiumPreview = lazy(() => import('@/pages/tools/StadiumPreview'));
const EffectsPreview = lazy(() => import('@/pages/tools/EffectsPreview'));
const MotionLab = import.meta.env.DEV ? lazy(() => import('@/pages/tools/MotionLab')) : null;

// Admin pages
import { AdminLayout } from '@/components/admin/AdminLayout';
import { AdminRoute } from '@/components/admin/AdminRoute';
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminReplays = lazy(() => import('@/pages/admin/AdminReplays'));
const AdminComments = lazy(() => import('@/pages/admin/AdminComments'));
const AdminAnalytics = lazy(() => import('@/pages/admin/AdminAnalytics'));
const AdminFeedbacks = lazy(() => import('@/pages/admin/AdminFeedbacks'));
const AdminActivity = lazy(() => import('@/pages/admin/AdminActivity'));
const AdminJobs = lazy(() => import('@/pages/admin/AdminJobs'));
const AdminTokens = lazy(() => import('@/pages/admin/AdminTokens'));
const AdminContactMessages = lazy(() => import('@/pages/admin/AdminContactMessages'));
const AdminAnnouncements = lazy(() => import('@/pages/admin/AdminAnnouncements'));
const AdminAnnouncementEdit = lazy(() => import('@/pages/admin/AdminAnnouncementEdit'));

const PageLoader = () => (
  <div className="bc-route-loader" role="status">Opening BallCam…</div>
);

const router = createBrowserRouter([
  ...(MotionLab ? [{
    path: '/tools/motion-lab',
    element: <Suspense fallback={<PageLoader />}><MotionLab /></Suspense>,
  }] : []),
  {
    path: '/tools/effects',
    element: <Suspense fallback={<PageLoader />}><EffectsPreview /></Suspense>,
  },
  {
    path: '/tools/stadium',
    element: <Suspense fallback={<PageLoader />}><StadiumPreview /></Suspense>,
  },
  // Viewer uses fullscreen layout (navbar but no footer, no padding)
  {
    path: '/viewer/:id',
    element: <Layout fullscreen />,
    children: [
      {
        index: true,
        element: <Viewer />,
      },
    ],
  },
  // ClipViewer uses fullscreen layout
  {
    path: '/clips/:id',
    element: <Layout fullscreen />,
    children: [
      {
        index: true,
        element: <ClipViewer />,
      },
    ],
  },
  // Hidden tools - Mesh Preview Tool (031-mesh-preview-tool)
  {
    path: '/tools/mesh-preview',
    element: <Layout fullscreen />,
    children: [
      {
        index: true,
        element: <MeshPreview />,
      },
    ],
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'upload',
        element: <Upload />,
      },
      {
        path: 'rank-lab',
        element: <RankLab />,
      },
      {
        path: 'join/:sessionId',
        element: <JoinParty />,
      },
      {
        path: 'replays',
        element: <Replays />,
      },
      {
        path: 'replays/:id',
        element: <ReplayDetail />,
      },
      {
        path: 'players/:id',
        element: <PlayerProfile />,
      },
      {
        path: 'faq/replay-quality',
        element: <ReplayQualityFaq />,
      },
      // Cheaters list page (032-cheat-detection)
      {
        path: 'cheaters',
        element: <Cheaters />,
      },
      // News (announcements)
      {
        path: 'news',
        element: <News />,
      },
      {
        path: 'news/:slug',
        element: <NewsDetail />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'register',
        element: <Register />,
      },
      {
        path: 'verify-email',
        element: <VerifyEmail />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'device',
        element: <DeviceAuth />,
      },
      {
        path: 'my-replays',
        element: <MyReplays />,
      },
      {
        path: 'clips',
        element: <BrowseClips />,
      },
      {
        path: 'my-clips',
        element: <MyClips />,
      },
      {
        path: 'clips/:id/edit',
        element: <ClipEdit />,
      },
      {
        path: 'profile',
        element: <Profile />,
      },
      {
        path: 'settings/tokens',
        element: <ApiTokens />,
      },
      {
        path: 'docs/api',
        element: <ApiDocs />,
      },
      {
        path: 'feedback',
        element: <FeedbackHub />,
      },
      {
        path: 'feedback/new',
        element: <NewFeedback />,
      },
      {
        path: 'feedback/:id',
        element: <FeedbackPost />,
      },
      {
        path: 'feedback/:id/edit',
        element: <EditFeedback />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },
      {
        path: 'privacy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'legal',
        element: <LegalNotice />,
      },
      // 404 catch-all route (022-seo-optimization)
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  // Admin routes with dedicated layout
  {
    path: '/admin',
    element: <AdminRoute />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          {
            index: true,
            element: <Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>,
          },
          {
            path: 'users',
            element: <Suspense fallback={<PageLoader />}><AdminUsers /></Suspense>,
          },
          {
            path: 'replays',
            element: <Suspense fallback={<PageLoader />}><AdminReplays /></Suspense>,
          },
          {
            path: 'comments',
            element: <Suspense fallback={<PageLoader />}><AdminComments /></Suspense>,
          },
          {
            path: 'analytics',
            element: <Suspense fallback={<PageLoader />}><AdminAnalytics /></Suspense>,
          },
          {
            path: 'feedbacks',
            element: <Suspense fallback={<PageLoader />}><AdminFeedbacks /></Suspense>,
          },
          {
            path: 'activity',
            element: <Suspense fallback={<PageLoader />}><AdminActivity /></Suspense>,
          },
          {
            path: 'jobs',
            element: <Suspense fallback={<PageLoader />}><AdminJobs /></Suspense>,
          },
          {
            path: 'tokens',
            element: <Suspense fallback={<PageLoader />}><AdminTokens /></Suspense>,
          },
          {
            path: 'contact-messages',
            element: <Suspense fallback={<PageLoader />}><AdminContactMessages /></Suspense>,
          },
          {
            path: 'announcements',
            element: <Suspense fallback={<PageLoader />}><AdminAnnouncements /></Suspense>,
          },
          {
            path: 'announcements/new',
            element: <Suspense fallback={<PageLoader />}><AdminAnnouncementEdit /></Suspense>,
          },
          {
            path: 'announcements/:id',
            element: <Suspense fallback={<PageLoader />}><AdminAnnouncementEdit /></Suspense>,
          },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}
